// src/components/Notification.js
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

const Notification = ({
    type = 'info',
    message,
    duration = 5000,
    onClose,
    isVisible = true
}) => {
    const [isShowing, setIsShowing] = useState(isVisible);

    // Auto-hide notification after duration
    useEffect(() => {
        setIsShowing(isVisible);

        if (isVisible && duration !== null) {
            const timer = setTimeout(() => {
                setIsShowing(false);
                if (onClose) onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [isVisible, duration, onClose]);

    if (!isShowing) return null;

    // Define styles based on notification type
    const typeStyles = {
        success: {
            bg: 'bg-green-50',
            border: 'border-green-400',
            icon: 'text-green-400',
            text: 'text-green-800',
            iconPath: (
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                />
            )
        },
        error: {
            bg: 'bg-red-50',
            border: 'border-red-400',
            icon: 'text-red-400',
            text: 'text-red-800',
            iconPath: (
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                />
            )
        },
        warning: {
            bg: 'bg-yellow-50',
            border: 'border-yellow-400',
            icon: 'text-yellow-400',
            text: 'text-yellow-800',
            iconPath: (
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
            )
        },
        info: {
            bg: 'bg-blue-50',
            border: 'border-blue-400',
            icon: 'text-blue-400',
            text: 'text-blue-800',
            iconPath: (
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            )
        }
    };

    const styles = typeStyles[type] || typeStyles.info;

    return (
        <div className={`rounded-md ${styles.bg} p-4 border-l-4 ${styles.border}`}>
            <div className="flex">
                <div className="flex-shrink-0">
                    <svg
                        className={`h-5 w-5 ${styles.icon}`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        {styles.iconPath}
                    </svg>
                </div>
                <div className="ml-3">
                    <p className={`text-sm ${styles.text}`}>{message}</p>
                </div>
                <div className="ml-auto pl-3">
                    <div className="-mx-1.5 -my-1.5">
                        <button
                            type="button"
                            onClick={() => {
                                setIsShowing(false);
                                if (onClose) onClose();
                            }}
                            className={`inline-flex ${styles.bg} rounded-md p-1.5 ${styles.text} hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400`}
                        >
                            <span className="sr-only">Dismiss</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

Notification.propTypes = {
    type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
    message: PropTypes.string.isRequired,
    duration: PropTypes.number,
    onClose: PropTypes.func,
    isVisible: PropTypes.bool
};

export default Notification;